import React from 'react';
import {
    Flex,
    useToast,
    Button,
    Menu,
    MenuContent,
    MenuSeparator,
    MenuTrigger,
    MenuItem,
    Spinner,
} from '@cad-arc';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useQuery } from '@tanstack/react-query';
import { MenuLoadingError } from '../common/components/menu-loading-error';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useGetWorkspaceWindowIdForLocation } from '../panels/workspace-helpers';
import { WorkspacesResource } from '@mark43/cad-api';
import { useIntegratedActionBroadcaster } from '../action-broadcaster/use-integrated-action-broadcaster';
import { v4 } from 'uuid';
import { useApplicationType } from '../common/hooks/use-application-type';
import { Tooltip } from '~/common/components/tooltip';

const TRAILING_ASTERISK = /\*$/gi;

export function WorkspaceManagementMenuContent(): React.ReactElement {
    const { isElectronRewrite, isCad } = useApplicationType();
    const { trigger } = useIntegratedActionBroadcaster();
    const apiClient = useApiClient();
    const modalControls = useFullscreenModalControls();
    const userId = useBootstrapState().userProfileView.userId;
    const workspacePanelsStore = useWorkspaceStore();
    const toast = useToast();
    const { postMessage } = useBroadcast('legacy-store-broadcast');
    const enabledFeatures = useEnabledFeatures();
    const location = useLocation();
    const query = useQuery({
        queryKey: ['workspace-management-menu'],
        queryFn: () => {
            return apiClient.request<WorkspacesResource.GetWorkspaceTemplateOptions>({
                method: 'GET',
                url: 'workspaces/options/',
            });
        },
    });

    const createBlankWorkspaceElectron = React.useCallback(() => {
        return apiClient.request<WorkspacesResource.CreateBlankWorkspaceForUser>({
            method: 'POST',
            url: 'workspaces/blank',
        });
    }, [apiClient]);
    const getWorkspace = React.useCallback(
        (workspaceId: number) => {
            return apiClient.request<WorkspacesResource.SelectWorkspaceTemplateForUser>({
                method: 'POST',
                url: `workspaces/select/template/${workspaceId}/${userId}/`,
            });
        },
        [apiClient, userId]
    );
    const getWorkspaceWindowIdForLocation = useGetWorkspaceWindowIdForLocation();
    const selectWorkspaceTemplate = React.useCallback(
        (workspaceId: number) => {
            getWorkspace(workspaceId)
                .then((response) => {
                    const workspace = response.data.data;
                    if (workspace) {
                        workspacePanelsStore.setWorkspace(
                            workspace,
                            isCad ? getWorkspaceWindowIdForLocation(location, workspace) : undefined
                        );
                    }
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: 'Failed to load workspace',
                        });
                    })
                );
        },
        [
            getWorkspace,
            getWorkspaceWindowIdForLocation,
            isCad,
            location,
            toast,
            workspacePanelsStore,
        ]
    );
    const createBlankWorkspaceWeb = React.useCallback(() => {
        return apiClient.request<WorkspacesResource.SelectBlankWorkspaceForUser>({
            method: 'POST',
            url: 'workspaces/blank/template',
        });
    }, [apiClient]);
    const saveWorkspace = React.useCallback(async () => {
        const activeWorkspaceWindowId = workspacePanelsStore.getActiveWorkspaceWindowId();
        if (activeWorkspaceWindowId) {
            workspacePanelsStore.enqueueSaveWorkspaceWindow(activeWorkspaceWindowId);
            try {
                await workspacePanelsStore.enqueueSaveWorkspaceWindow.flush();
            } catch (e) {
                // still continue to set previously saved user workspace as template
            }
        }
        return apiClient.request<WorkspacesResource.UpdateUserWorkspaceTemplate>({
            method: 'PUT',
            url: 'workspaces/user/template',
        });
    }, [apiClient, workspacePanelsStore]);

    const electronSelectWorkspace = React.useCallback(
        (workspaceValue: number) => {
            if (isElectronRewrite) {
                trigger([
                    {
                        id: v4(),
                        targetWindowType: 'ROOT',
                        type: 'SELECT_WORKSPACE',
                        payload: workspaceValue,
                    },
                ]);
            } else {
                postMessage({
                    type: 'LOAD_NEW_WORKSPACE',
                    payload: workspaceValue,
                });
            }
        },
        [trigger, postMessage, isElectronRewrite]
    );

    const { isCadElectron } = useApplicationType();

    if (query.isLoading) {
        return (
            <Flex justifyContent="center" alignItems="center">
                <Spinner />
            </Flex>
        );
    }

    const data = query.data?.data.data;

    if (query.isError || !data) {
        return <MenuLoadingError />;
    }

    return (
        <>
            {data.userWorkspaces.map((workspaceOption) => (
                <MenuItem
                    key={workspaceOption.value}
                    onSelect={() => {
                        if (isCadElectron) {
                            electronSelectWorkspace(workspaceOption.value);
                        } else {
                            selectWorkspaceTemplate(workspaceOption.value);
                        }
                    }}
                >
                    {workspaceOption.label}
                </MenuItem>
            ))}
            {!!data.userWorkspaces.length && <MenuSeparator />}
            {data.departmentWorkspaces.map((workspaceOption) => (
                <MenuItem
                    key={workspaceOption.value}
                    onSelect={() => {
                        if (isCadElectron) {
                            electronSelectWorkspace(workspaceOption.value);
                        } else {
                            selectWorkspaceTemplate(workspaceOption.value);
                        }
                    }}
                >
                    {workspaceOption.label}
                </MenuItem>
            ))}
            {!!data.departmentWorkspaces.length && <MenuSeparator />}
            <MenuItem
                data-testid="new-workspace-btn"
                onSelect={() => {
                    if (isCadElectron) {
                        createBlankWorkspaceElectron()
                            .then((response) => {
                                const data = response.data.data;
                                if (data) {
                                    electronSelectWorkspace(data.id);
                                }
                            })
                            .catch(
                                createApiErrorHandler(() => {
                                    toast({
                                        status: 'error',
                                        description: 'Failed to create new workspace.',
                                    });
                                })
                            );
                    } else {
                        createBlankWorkspaceWeb()
                            .then((response) => {
                                const workspace = response.data.data;
                                if (workspace) {
                                    workspacePanelsStore.setWorkspace(
                                        workspace,
                                        isCad
                                            ? getWorkspaceWindowIdForLocation(location, workspace)
                                            : undefined
                                    );
                                }
                            })
                            .catch(
                                createApiErrorHandler(() => {
                                    toast({
                                        status: 'error',
                                        description: 'Failed to create new workspace.',
                                    });
                                })
                            );
                    }
                }}
            >
                New
            </MenuItem>
            <MenuItem
                data-testid="save-workspace-btn"
                onSelect={() => {
                    saveWorkspace().catch(
                        createApiErrorHandler(() => {
                            toast({
                                status: 'error',
                                description: 'Failed to save workspace.',
                            });
                        })
                    );
                }}
            >
                Save
            </MenuItem>
            <MenuItem
                onSelect={() => {
                    modalControls.open({
                        type: 'SAVE_WORKSPACE_AS',
                        props: {
                            workspaceTemplateType: 'USER',
                        },
                    });
                }}
            >
                Save As...
            </MenuItem>
            <MenuItem
                data-testid="manage-workspaces-btn"
                onSelect={() => {
                    modalControls.open({
                        type: 'MANAGE_USER_WORKSPACES_MODAL',
                        props: {
                            workspaceOptions: data.userWorkspaces,
                        },
                    });
                }}
            >
                Manage My Workspaces...
            </MenuItem>
            <MenuSeparator />
            {enabledFeatures.workspaceAdmin ? (
                <>
                    <MenuItem
                        onSelect={() => {
                            modalControls.open({
                                type: 'SAVE_WORKSPACE_AS',
                                props: {
                                    workspaceTemplateType: 'DEPARTMENT',
                                },
                            });
                        }}
                    >
                        Save as Department Template...
                    </MenuItem>
                    <MenuItem
                        onSelect={() => {
                            modalControls.open({
                                type: 'MANAGE_DEPARTMENT_WORKSPACES_MODAL',
                                props: {
                                    defaultDepartmentTemplate: data.defaultDepartmentTemplate,
                                    workspaceOptions: data.departmentWorkspaces,
                                },
                            });
                        }}
                    >
                        Manage Department Templates...
                    </MenuItem>
                    <MenuItem
                        onSelect={() => {
                            modalControls.open({
                                type: 'RESET_USER_WORKSPACE_MODAL',
                                props: {
                                    userOptions: [],
                                },
                            });
                        }}
                    >
                        Reset User Workspace
                    </MenuItem>
                </>
            ) : undefined}
        </>
    );
}

export const WorkspaceManagementMenu = observer(
    function WorkspaceManagementMenu(): React.ReactElement {
        const workspacePanelsStore = useWorkspaceStore();
        const formattedWorkspaceName = (workspacePanelsStore.workspaceName.length > 15
            ? `${workspacePanelsStore.workspaceName.substring(0, 12)}...`
            : workspacePanelsStore.workspaceName
        ).replace(TRAILING_ASTERISK, '');
        return (
            <Menu>
                <MenuTrigger data-testid="workspace-menu">
                    <Tooltip overlay={workspacePanelsStore.workspaceName}>
                        <Button variant="ghost" leadingVisual="Workspace">
                            {formattedWorkspaceName}
                        </Button>
                    </Tooltip>
                </MenuTrigger>
                <MenuContent style={{ maxHeight: '600px', overflowY: 'scroll' }}>
                    <WorkspaceManagementMenuContent />
                </MenuContent>
            </Menu>
        );
    }
);
